/*
 *  (c) 2015 - 2025 ENISCO by FORCAM GmbH
 */

import React, {Fragment, ReactNode, useState} from 'react'
import {FormControlLabel, Grid, Switch, TextField, Typography} from '@mui/material'
import {FormattedMessage, useIntl} from 'react-intl'
import {clone, SeverityConfiguration, SeverityType} from '@enisco/mcc-react-lib'

interface Props {
  config: SeverityConfiguration
  onChange: (configuration: SeverityConfiguration, type: SeverityType) => void
  type: SeverityType
}

const getNotificationType = (notificationType: string): ReactNode => {
  switch (notificationType) {
    case 'info':
      return (
        <FormattedMessage
          id='MyProfileDialog.InfoNotification'
          defaultMessage='information'
        />
      )
    case 'success':
      return (
        <FormattedMessage
          id='MyProfileDialog.SuccessNotification'
          defaultMessage='success'
        />
      )
    case 'warning':
      return (
        <FormattedMessage
          id='MyProfileDialog.WarningNotification'
          defaultMessage='warning'
        />
      )
    case 'error':
      return (
        <FormattedMessage
          id='MyProfileDialog.ErrorNotification'
          defaultMessage='error'
        />
      )
  }
  return <Fragment />
}

const NotificationTab: React.FC<Props> = ({config, onChange, type}) => {
  const intl = useIntl()
  const [checked, setChecked] = useState<boolean>(config.on)
  const [duration, setDuration] = useState<number | undefined>(
    config.autoHideDuration ? config.autoHideDuration / 1000 : undefined
  )
  const regex = /^\d*$/

  const updateChecked = (newChecked: boolean) => {
    setChecked(newChecked)
    saveData(newChecked, duration)
  }

  const updateDuration = (newDuration: string) => {
    const time = newDuration ? parseInt(newDuration) : undefined
    if (time !== duration) {
      setDuration(time)
      saveData(checked, time)
    }
  }

  const saveData = (newChecked: boolean, newDuration: number | undefined) => {
    const updateConfig = clone(config)
    updateConfig.on = newChecked
    updateConfig.autoHideDuration =
      newChecked && newDuration ? newDuration * 1000 : undefined
    onChange(updateConfig, type)
  }

  return (
    <>
      <Typography variant='subtitle1'>{getNotificationType(type)}</Typography>

      <Grid container alignItems={'center'}>
        <Grid item xs={6}>
          <FormControlLabel
            sx={{width: '100%'}}
            control={
              <Switch
                checked={checked}
                onChange={(event) => {
                  updateChecked(event.target.checked)
                }}
                name={`change-${type}`}
                data-testid={`NotificationTab:change_${type}`}
              />
            }
            label={intl.formatMessage({
              id: 'MyProfileDialog.ShowNotification',
              defaultMessage: 'Show notification'
            })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            sx={checked ? {width: '100%'} : {display: 'none'}}
            value={duration}
            onChange={(event) => {
              if (regex.test(event.target.value)) {
                updateDuration(event.target.value)
              } else {
                if (!duration) {
                  setDuration(0)
                }
              }
            }}
            id='standard-basic'
            label={intl.formatMessage({
              id: 'MyProfileDialog.NotificationDuration',
              defaultMessage: 'Display duration (in s)'
            })}
            inputProps={{
              inputMode: 'numeric',
              maxLength: 2,
              'data-testid': `NotificationTab:duration_${type}`
            }}
          />
        </Grid>
      </Grid>
    </>
  )
}

export default NotificationTab
